import React from 'react';

import Wahanda from 'common/wahanda';
import { EmployeeSalesReportAnalytics } from 'common/analytics';
import { CHANNEL } from '../BookingChannelFilter';
import { buildSalesReportDataCells } from '../common/buildSalesReportDataCells';
import { salesReportHeaderRows, SalesReportMetrics } from '../common/salesReportTableLayout';
import SalesReportHashLink from '../SalesReport/hashLink';
import type { SharedProps } from 'reconnect-types/moduleFederation';

type EmployeeSalesRow = SalesReportMetrics & {
  employee?: string;
  employeeId?: number;
};

type EmployeeSalesReportData = {
  rows: EmployeeSalesRow[];
  total: SalesReportMetrics;
};

export type EmployeeSalesReportTableProps = {
  venueId: string;
  fromDate: any;
  toDate: any;
  reportData: EmployeeSalesReportData;
};

const employeeSalesDataCellTestIds = {
  servicesQty: 'employee-sales-services-quantity',
  servicesGross: 'employee-sales-services-gross',
  productsQty: 'employee-sales-products-quantity',
  productsGross: 'employee-sales-products-gross',
  voucherQuantity: 'employee-sales-vouchers-quantity',
  voucherGross: 'employee-sales-vouchers-gross',
  prepaid: 'employee-sales-prepaid',
  total: 'employee-sales-gross-total',
};

export const getEmployeeSalesReportTableProps = ({
  venueId,
  fromDate,
  toDate,
  reportData,
}: EmployeeSalesReportTableProps): SharedProps['ReportTable'] => {
  const headings = Wahanda.lang.reports.sales.employeeSalesReport.headings;
  const includingVat = headings.includingVat;

  const headerRows = salesReportHeaderRows({
    firstColumn: headings.employee,
    services: headings.services,
    products: headings.products,
    vouchers: headings.vouchers,
    // `prepaid` is only defined under productSalesReport in the locale files.
    prepaid: Wahanda.lang.reports.sales.productSalesReport.headings.prepaid,
    total: `${headings.totalSales} ${includingVat}`,
    quantity: headings.quantity,
    sales: `${headings.sales} ${includingVat}`,
  });

  const tableRows = reportData.rows.map((row) => ({
    id: row.employee ?? '',
    dataTestId: 'employee-sales-row',
    cells: [
      {
        content: (
          <SalesReportHashLink
            venueId={venueId}
            fromDate={fromDate}
            toDate={toDate}
            employeeFilter={row.employeeId}
            selectedChannel={CHANNEL.ALL}
            onClick={EmployeeSalesReportAnalytics.trackEmployeeLinkClick}
          >
            {row.employee ?? ''}
          </SalesReportHashLink>
        ),
        dataTestId: 'employee-sales-name',
      },
      ...buildSalesReportDataCells(row, employeeSalesDataCellTestIds),
    ],
  }));

  const footerRow = {
    cells: [
      { content: headings.grandTotal },
      ...buildSalesReportDataCells(reportData.total, employeeSalesDataCellTestIds).map((cell) => ({
        ...cell,
        variant: 'grandTotal' as const,
      })),
    ],
  };

  return {
    headerRows,
    rows: tableRows,
    footerRow,
    footerDataTestId: 'employee-sales-totals',
    testID: 'employee-sales-report-table',
  };
};
